import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    Avatar,
    useTheme,
    AppBar,
    Toolbar,
    IconButton,
    Grid,
    Paper,
    Chip,
    CircularProgress,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Fade,
    Zoom,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HistoryIcon from '@mui/icons-material/History';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { api, isAuthenticated } from '../utils/api';

interface CreditBalance {
    available_credits: number;
    pending_credits: number;
    lifetime_earned: number;
    lifetime_purchased: number;
    lifetime_redeemed: number;
}

interface CreditPackage {
    package_id: string;
    name: string;
    credits_amount: number;
    price_usd: number;
    bonus_percentage: number;
    is_popular?: boolean;
}

interface Transaction {
    transaction_id: string;
    amount: number;
    type: 'earned' | 'purchased' | 'redeemed' | 'expired' | 'bonus';
    description: string;
    created_at: string;
}

const CreditsPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState<CreditBalance | null>(null);
    const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
    const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);

    // Credit packages (will come from API later)
    const [packages] = useState<CreditPackage[]>([
        {
            package_id: '1',
            name: 'Starter',
            credits_amount: 100,
            price_usd: 85,
            bonus_percentage: 15,
        },
        {
            package_id: '2',
            name: 'Popular',
            credits_amount: 500,
            price_usd: 400,
            bonus_percentage: 25,
            is_popular: true,
        },
        {
            package_id: '3',
            name: 'Premium',
            credits_amount: 1000,
            price_usd: 750,
            bonus_percentage: 33,
        },
        {
            package_id: '4',
            name: 'VIP',
            credits_amount: 2500,
            price_usd: 1750,
            bonus_percentage: 43,
        },
    ]);

    // Mock transactions (will come from API later)
    const [transactions] = useState<Transaction[]>([
        {
            transaction_id: '1',
            amount: 250,
            type: 'purchased',
            description: 'Credit package purchase',
            created_at: new Date().toISOString(),
        },
    ]);

    const fetchCreditsBalance = useCallback(async () => {
        try {
            if (!isAuthenticated()) {
                navigate('/login', { replace: true });
                return;
            }

            const response = await api.get('/credits');

            if (response.ok) {
                const data = await response.json();
                setBalance(data);
            }
        } catch (error) {
            console.error('Failed to fetch credits balance:', error);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/login', { replace: true });
            return;
        }

        fetchCreditsBalance();
    }, [navigate, fetchCreditsBalance]);

    const handlePackageSelect = (pkg: CreditPackage) => {
        setSelectedPackage(pkg);
        setPurchaseDialogOpen(true);
    };

    const handlePurchase = () => {
        // TODO: Integrate Stripe payment
        alert('Stripe integration coming soon! This will process your payment.');
        setPurchaseDialogOpen(false);
    };

    const getTransactionIcon = (type: string) => {
        switch (type) {
            case 'purchased':
                return <AddCircleOutlineIcon sx={{ color: '#4CAF50' }} />;
            case 'earned':
                return <EmojiEventsIcon sx={{ color: '#FF9800' }} />;
            case 'redeemed':
                return <RemoveCircleOutlineIcon sx={{ color: '#2196F3' }} />;
            case 'expired':
                return <RemoveCircleOutlineIcon sx={{ color: '#F44336' }} />;
            case 'bonus':
                return <CardGiftcardIcon sx={{ color: '#4A6FA5' }} />;
            default:
                return <HistoryIcon />;
        }
    };

    const getTransactionColor = (type: string) => {
        switch (type) {
            case 'purchased':
            case 'earned':
            case 'bonus':
                return '#4CAF50';
            case 'redeemed':
            case 'expired':
                return '#F44336';
            default:
                return 'inherit';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress sx={{ color: theme.palette.primary.main }} size={56} />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
            {/* Header */}
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    borderBottom: '1px solid rgba(0,0,0,0.05)',
                }}
            >
                <Toolbar>
                    <IconButton
                        onClick={() => navigate('/explore')}
                        sx={{ mr: 2 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 800,
                            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        My Credits
                    </Typography>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Balance Card */}
                <Fade in timeout={600}>
                    <Card
                        sx={{
                            mb: 4,
                            borderRadius: '24px',
                            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                            color: '#fff',
                            boxShadow: '0 20px 60px rgba(255, 165, 0, 0.4)',
                        }}
                    >
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Avatar
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.3)',
                                        width: 64,
                                        height: 64,
                                    }}
                                >
                                    <AccountBalanceWalletIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                                        Available Balance
                                    </Typography>
                                    <Typography variant="h2" sx={{ fontWeight: 900 }}>
                                        ${balance?.available_credits.toFixed(2) || '0.00'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Grid container spacing={3}>
                                <Grid item xs={6} sm={3}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            borderRadius: '16px',
                                            backdropFilter: 'blur(10px)',
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                            Pending
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            ${balance?.pending_credits.toFixed(2) || '0.00'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            borderRadius: '16px',
                                            backdropFilter: 'blur(10px)',
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                            Lifetime Earned
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            ${balance?.lifetime_earned.toFixed(2) || '0.00'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            borderRadius: '16px',
                                            backdropFilter: 'blur(10px)',
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                            Lifetime Purchased
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            ${balance?.lifetime_purchased.toFixed(2) || '0.00'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            borderRadius: '16px',
                                            backdropFilter: 'blur(10px)',
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                            Lifetime Redeemed
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            ${balance?.lifetime_redeemed.toFixed(2) || '0.00'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Fade>

                {/* How Credits Work */}
                <Fade in timeout={800}>
                    <Alert
                        severity="info"
                        sx={{
                            mb: 4,
                            borderRadius: '16px',
                            '& .MuiAlert-icon': { fontSize: 28 }
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                            💡 How Credits Work
                        </Typography>
                        <Typography variant="body2">
                            Use credits to unlock exclusive pricing on hotels, flights, and experiences.
                            Earn credits by booking travel, referring friends, or purchasing packages below.
                            Higher tier members earn more credits per booking!
                        </Typography>
                    </Alert>
                </Fade>

                {/* Credit Packages */}
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>
                    💳 Buy Credits
                </Typography>

                <Grid container spacing={3} sx={{ mb: 5 }}>
                    {packages.map((pkg, index) => (
                        <Grid item xs={12} sm={6} md={3} key={pkg.package_id}>
                            <Zoom in timeout={300 + index * 100}>
                                <Card
                                    onClick={() => handlePackageSelect(pkg)}
                                    sx={{
                                        cursor: 'pointer',
                                        borderRadius: '20px',
                                        height: '100%',
                                        position: 'relative',
                                        overflow: 'visible',
                                        transition: 'all 0.3s ease',
                                        border: pkg.is_popular ? '3px solid #2D6E6A' : '1px solid #e0e0e0',
                                        boxShadow: pkg.is_popular
                                            ? '0 12px 40px rgba(0, 180, 160, 0.3)'
                                            : '0 8px 32px rgba(0,0,0,0.1)',
                                        '&:hover': {
                                            transform: 'translateY(-8px) scale(1.02)',
                                            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                                        },
                                    }}
                                >
                                    {pkg.is_popular && (
                                        <Chip
                                            label="MOST POPULAR"
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: -12,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                bgcolor: '#2D6E6A',
                                                color: '#fff',
                                                fontWeight: 'bold',
                                                fontSize: '0.7rem',
                                            }}
                                        />
                                    )}
                                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 'bold',
                                                mb: 1,
                                                color: pkg.is_popular ? '#2D6E6A' : 'inherit'
                                            }}
                                        >
                                            {pkg.name}
                                        </Typography>

                                        <Box sx={{ mb: 2 }}>
                                            <Typography
                                                variant="h3"
                                                sx={{
                                                    fontWeight: 900,
                                                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                }}
                                            >
                                                ${pkg.credits_amount}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                in credits
                                            </Typography>
                                        </Box>

                                        <Chip
                                            icon={<LocalOfferIcon />}
                                            label={`${pkg.bonus_percentage}% bonus`}
                                            sx={{
                                                mb: 2,
                                                bgcolor: '#FFF4E6',
                                                color: '#F39C12',
                                                fontWeight: 'bold',
                                            }}
                                        />

                                        <Divider sx={{ my: 2 }} />

                                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            ${pkg.price_usd}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            ${(pkg.price_usd / pkg.credits_amount).toFixed(2)} per credit
                                        </Typography>

                                        <Button
                                            variant={pkg.is_popular ? 'contained' : 'outlined'}
                                            fullWidth
                                            sx={{
                                                borderRadius: '25px',
                                                textTransform: 'none',
                                                fontWeight: 'bold',
                                                py: 1.5,
                                                ...(pkg.is_popular && {
                                                    background: 'linear-gradient(135deg, #2D6E6A 0%, #00D4B8 100%)',
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #00D4B8 0%, #00E4C8 100%)',
                                                    }
                                                })
                                            }}
                                        >
                                            Buy Now
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Zoom>
                        </Grid>
                    ))}
                </Grid>

                {/* Earning Credits */}
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>
                    🎯 Ways to Earn Credits
                </Typography>

                <Grid container spacing={3} sx={{ mb: 5 }}>
                    {[
                        { icon: <ShoppingCartIcon />, title: 'Book Travel', desc: 'Earn 2-7% back on every booking', color: '#2196F3' },
                        { icon: <CardGiftcardIcon />, title: 'Refer Friends', desc: 'Get $25 when friends book', color: '#4A6FA5' },
                        { icon: <StarIcon />, title: 'Write Reviews', desc: 'Earn $5 per verified review', color: '#FF9800' },
                        { icon: <TrendingUpIcon />, title: 'Reach VIP', desc: 'Unlock 7% credit rate', color: '#4CAF50' },
                    ].map((item, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Fade in timeout={500 + index * 100}>
                                <Paper
                                    sx={{
                                        p: 3,
                                        borderRadius: '20px',
                                        textAlign: 'center',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                                        }
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: `${item.color}20`,
                                            color: item.color,
                                            width: 56,
                                            height: 56,
                                            mx: 'auto',
                                            mb: 2,
                                        }}
                                    >
                                        {item.icon}
                                    </Avatar>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        {item.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {item.desc}
                                    </Typography>
                                </Paper>
                            </Fade>
                        </Grid>
                    ))}
                </Grid>

                {/* Transaction History */}
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>
                    📜 Transaction History
                </Typography>

                <Card sx={{ borderRadius: '20px', mb: 4 }}>
                    {transactions.length === 0 ? (
                        <CardContent sx={{ textAlign: 'center', py: 6 }}>
                            <HistoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                No transactions yet
                            </Typography>
                            <Typography variant="body2" color="text.disabled">
                                Your credit activity will appear here
                            </Typography>
                        </CardContent>
                    ) : (
                        <List>
                            {transactions.map((txn, index) => (
                                <React.Fragment key={txn.transaction_id}>
                                    <ListItem sx={{ py: 2 }}>
                                        <ListItemIcon>
                                            {getTransactionIcon(txn.type)}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                    {txn.description}
                                                </Typography>
                                            }
                                            secondary={formatDate(txn.created_at)}
                                        />
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 'bold',
                                                color: getTransactionColor(txn.type),
                                            }}
                                        >
                                            {txn.type === 'redeemed' || txn.type === 'expired' ? '-' : '+'}
                                            ${txn.amount.toFixed(2)}
                                        </Typography>
                                    </ListItem>
                                    {index < transactions.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Card>
            </Container>

            {/* Purchase Dialog */}
            <Dialog
                open={purchaseDialogOpen}
                onClose={() => setPurchaseDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                {selectedPackage && (
                    <>
                        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                Purchase {selectedPackage.name} Package
                            </Typography>
                        </DialogTitle>
                        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
                            <Box
                                sx={{
                                    py: 3,
                                    px: 4,
                                    mb: 3,
                                    borderRadius: '16px',
                                    background: 'linear-gradient(135deg, #FFF4E6 0%, #FFE4CC 100%)',
                                }}
                            >
                                <Typography variant="h2" sx={{ fontWeight: 900, color: '#F39C12' }}>
                                    ${selectedPackage.credits_amount}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    in credits
                                </Typography>
                                <Chip
                                    icon={<CardGiftcardIcon />}
                                    label={`Includes ${selectedPackage.bonus_percentage}% bonus!`}
                                    sx={{ mt: 2, bgcolor: '#fff' }}
                                />
                            </Box>

                            <List>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="Credits never expire" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="Use on any booking" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="Instant delivery to your account" />
                                </ListItem>
                            </List>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography>Package Price</Typography>
                                <Typography sx={{ fontWeight: 'bold' }}>${selectedPackage.price_usd.toFixed(2)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography color="success.main">Bonus Credits</Typography>
                                <Typography sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                    +${(selectedPackage.credits_amount * selectedPackage.bonus_percentage / 100).toFixed(2)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: '#f5f5f5', borderRadius: '12px' }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Total Credits</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#F39C12' }}>
                                    ${(selectedPackage.credits_amount * (1 + selectedPackage.bonus_percentage / 100)).toFixed(2)}
                                </Typography>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ p: 3, pt: 0 }}>
                            <Button
                                onClick={() => setPurchaseDialogOpen(false)}
                                sx={{ textTransform: 'none' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handlePurchase}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '25px',
                                    px: 4,
                                    py: 1.5,
                                    fontWeight: 'bold',
                                    background: 'linear-gradient(135deg, #2D6E6A 0%, #00D4B8 100%)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #00D4B8 0%, #00E4C8 100%)',
                                    }
                                }}
                            >
                                Pay ${selectedPackage.price_usd.toFixed(2)}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default CreditsPage;
